import React, { useEffect, useRef, useState } from 'react';
import { Modal, Spin, Button, Input, Form, Alert, List, Typography } from 'antd';
import { resumoSiteTech as fetchResumoSiteTech } from '../lib/getResumoSite';
import { SearchResultData } from './SearchResultTable';
import Title from "antd/lib/typography/Title";

const { Paragraph, Text } = Typography;

interface TechModalProps {
    setSelectedRecord: (record: SearchResultData | null) => void;
    setIsTechModalVisible: (visible: boolean) => void;
    isTechModalVisible: boolean;
    selectedRecord: SearchResultData | null;
}

interface Script {
    nome: string;
    descricao: string;
    codigo_identificado: string;
}

interface ResumoSiteTech {
    scripts: Script[];
    resumo: string;
}

interface ApiResponse<T> {
    data?: T;
    error?: string;
}

const TechModal: React.FC<TechModalProps> = ({
    isTechModalVisible,
    selectedRecord,
    setSelectedRecord,
    setIsTechModalVisible,
}) => {
    const latestRequestId = useRef(0);
    const [step, setStep] = useState<'confirm' | 'result'>('confirm');
    const [domain, setDomain] = useState<string>(selectedRecord?.dominio_email.toLowerCase() || '');
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<ApiResponse<ResumoSiteTech> | null>(null);
    const [apiError, setApiError] = useState<string | null>(null);

    useEffect(() => {
        if (isTechModalVisible) {
            setStep('confirm');
            setDomain(selectedRecord?.dominio_email.toLowerCase() || '');
            setData(null);
            setApiError(null);
            setLoading(false);
        }
    }, [isTechModalVisible, selectedRecord]);

    const handleClose = () => {
        setIsTechModalVisible(false);
        setSelectedRecord(null);
    };

    const handleGoBack = () => {
        setStep('confirm');
        setApiError(null);
        setData(null);
    };

    const handleConfirm = async () => {
        
        if (!domain) return;
        const cacheKey = `${selectedRecord?.cnpj_completo}/site:${domain}`;
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
            const cachedData = JSON.parse(cached) as ApiResponse<ResumoSiteTech>;
            if (cachedData.error || (cachedData.data && cachedData.data.scripts.length === 0)) {
                setApiError(cachedData.error || 'Nenhum script encontrado.');
                setData(null);
                setStep('result');
                return;
            }
            setData(cachedData);
            setStep('result');
            setApiError(null);
            return;
        }
        const requestId = ++latestRequestId.current;
        setLoading(true);
        setApiError(null);
        try {
            const response = await fetchResumoSiteTech(domain);
            if (requestId !== latestRequestId.current) return;
            if (response && response.scripts !== undefined) {
                if (response.scripts.length === 0) {
                    const errorResponse: ApiResponse<ResumoSiteTech> = { error: 'Nenhum script encontrado.' };
                    setData(errorResponse);
                    localStorage.setItem(cacheKey, JSON.stringify(errorResponse));
                    setStep('result');
                } else {
                    const successResponse: ApiResponse<ResumoSiteTech> = { data: response };
                    setData(successResponse);
                    localStorage.setItem(cacheKey, JSON.stringify(successResponse));
                    setStep('result');
                }
            } else {
                const errorResponse: ApiResponse<ResumoSiteTech> = { error: 'Resposta da API inválida.' };
                setData(errorResponse);
                localStorage.setItem(cacheKey, JSON.stringify(errorResponse));
                setStep('result');
            }
        } catch (error: any) {
            if (requestId !== latestRequestId.current) return;
            console.error(error);
            let errorMessage = 'Erro ao buscar dados do site.';
            if (error.message) {
                errorMessage = error.message;
            } else if (error.response && error.response.status) {
                errorMessage = `Erro na API: Status ${error.response.status}`;
                if (error.response.data && error.response.data.message) {
                    errorMessage += ` - ${error.response.data.message}`;
                }
            }
            setApiError(errorMessage);
            setData(null);
            setStep('result');
        } finally {
            if (requestId === latestRequestId.current) {
                setLoading(false);
              }
        }
    };

    return (
        <Modal
            title={"Tech: " + domain}
            centered
            open={isTechModalVisible}
            width={step === 'confirm' ? '40%' : '80%'}
            onCancel={handleClose}
            cancelButtonProps={{hidden: true}}
            footer={
                loading
                    ? null
                    : step === 'confirm'
                        ? [
                            // <Button key="cancel" onClick={handleClose}>
                            //     Cancelar
                            // </Button>,
                            <Button
                                key="confirm"
                                type="primary"
                                disabled={!domain.trim()}
                                onClick={handleConfirm}
                                className='submitButton'
                            >
                                Confirmar
                            </Button>,
                        ]
                        : [
                            <Button key="close" type="primary" onClick={handleGoBack} className='submitButton'>
                                Voltar
                            </Button>,
                        ]
            }
        >
            {loading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Spin tip="Gerando resumo..." spinning />
                </div>
            ) : step === 'confirm' ? (
                <Form layout="vertical">
                    <Form.Item label="Digite ou confirme o domínio do site:">
                        <Input
                            style={{borderRadius: '30px'}}
                            value={domain}
                            onChange={(e) => setDomain(e.target.value)}
                            onPressEnter={handleConfirm}
                            placeholder="ex: exemplo.com"
                        />
                    </Form.Item>
                </Form>
            ) : apiError ? (
                <Alert
                    message="Erro ao buscar dados do site"
                    description={apiError}
                    type="error"
                    showIcon
            
                />
            ) : data && data.data ? (
                <>
                    <div
                        style={{
                            padding: '15px',
                            border: '1px solid #d9d9d9',
                            marginBottom: '15px',
                            borderRadius: '4px',
                            backgroundColor: '#fafafa',
                            flex: 1,
                        }}
                    >
                        <Title level={5}>Resumo Geral</Title>
                        <Paragraph>{data.data.resumo}</Paragraph>
                    </div>
                    <div
                        style={{
                            padding: '15px',
                            border: '1px solid #d9d9d9',
                            marginBottom: '15px',
                            borderRadius: '4px',
                            backgroundColor: '#fafafa',
                            flex: 1,
                        }}
                    >
                        <Title level={5}>Principais Tecnologias Utilizadas</Title>
                        <List
                            itemLayout="vertical"
                            dataSource={data.data.scripts}
                            renderItem={(script, index) => (
                                <List.Item>
                                    <List.Item.Meta
                                        title={<Text strong>{`${index + 1}. ${script.nome}`}</Text>}
                                        description={
                                            <>
                                                <Paragraph>{script.descricao}</Paragraph>
                                                <Text code>{script.codigo_identificado}</Text>
                                            </>
                                        }
                                    />
                                </List.Item>
                            )}
                        />
                    </div>
                </>
            ) : (
                data && data.error && (
                    <Alert
                        message="A URL informada não possui scripts identificados."
                        description={data.error}
                        type="warning"
                        showIcon
                        
                    />
                )
            )}
        </Modal>
    );
};

export default TechModal;